import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { crearProducto, actualizarProducto, getProductoById } from "../../../services/usuarios_tienda/productos";
import { obtenerCategoriasActivas } from "../../../services/usuarios_tienda/categorias";
import { getSubcategoriasActivasPorCategoria } from "../../../services/usuarios_tienda/subcategorias";
import { getMarcas } from "../../../services/usuarios_tienda/marcas";
import { obtenerUnidadesPesoActivas } from "../../../services/usuarios_tienda/unidades_peso";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import { fileURL } from "../../../utils/fileURL";
import { obtenerMonedaTienda } from "../../../utils/moneda";
import { getProductSku, slugifySkuPreview } from "../../../utils/slugifySkuPreview";
import {
  SummaryRow,
  ToolbarButton,
  ToolbarDivider,
  Input,
  SearchSelect,
} from "./ProductFormControls";
import "./ProductFormModal.css";
import "./ProductFormPage.css";

const PRODUCTS_LIST_PATH = "/administracion/tienda/products";
const IMAGE_SLOT_COUNT = 5;
const emptyImageSlots = () => Array.from({ length: IMAGE_SLOT_COUNT }, () => null);

// Convierte un valor de fecha del backend a "YYYY-MM-DDTHH:mm" para <input datetime-local>.
const toDatetimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

function ProductFormPage() {
  const navigate = useNavigate();
  const { productoId } = useParams();
  const isEdit = Boolean(productoId);

  const descriptionRef = useRef(null);
  const blobBySlotRef = useRef(emptyImageSlots());

  const [product, setProduct] = useState(null);
  const [configuracionTienda, setConfiguracionTienda] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);

  const simboloMoneda = useMemo(
    () => obtenerMonedaTienda(configuracionTienda).simbolo,
    [configuracionTienda]
  );

  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    modelo: "",
    no_parte: "",
    categoria_producto_id: "",
    subcategoria_id: "",
    marca_producto_id: "",
    precio_unitario: "",
    core_charge: "",
    oferta_activa: false,
    oferta_tipo: "porcentaje",
    oferta_valor: "",
    oferta_inicio: "",
    oferta_fin: "",
    peso: "",
    unidad_peso_id: "",
    estado: 0,
    destacado: false,
  });

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [slotFiles, setSlotFiles] = useState(emptyImageSlots);
  const [slotKeepUrls, setSlotKeepUrls] = useState(emptyImageSlots);
  const [slotDisplayUrl, setSlotDisplayUrl] = useState(emptyImageSlots);
  const [pdfFile, setPdfFile] = useState(null);

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [loadingSubcategorias, setLoadingSubcategorias] = useState(false);
  const [alert, setAlert] = useState(null);

  const getNiceErrorMessage = (error, fallback = "Error inesperado.") => {
    const msg = error?.response?.data?.message || error?.message || fallback;
    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 260 ? clean.slice(0, 260) + "…" : clean;
  };

  const revokeAllSlotBlobs = () => {
    blobBySlotRef.current.forEach((u) => {
      if (u && String(u).startsWith("blob:")) URL.revokeObjectURL(u);
    });
    blobBySlotRef.current = emptyImageSlots();
  };

  const updateField = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "categoria_producto_id") {
        return {
          ...prev,
          categoria_producto_id: value,
          subcategoria_id: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const cargarSubcategorias = async (categoriaId) => {
    const categoria = Number(categoriaId);

    if (!categoria) {
      setSubcategorias([]);
      return;
    }

    try {
      setLoadingSubcategorias(true);
      const data = await getSubcategoriasActivasPorCategoria(categoria);
      setSubcategorias(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error cargando subcategorías:", err);
      setSubcategorias([]);
      setAlert({
        type: "error",
        message: getNiceErrorMessage(err, "No se pudieron cargar las subcategorías."),
      });
    } finally {
      setLoadingSubcategorias(false);
    }
  };

  // Carga inicial: configuración, combos y (si es edición) el producto.
  useEffect(() => {
    let alive = true;

    setLoadingPage(true);
    setAlert(null);
    setSaving(false);
    setToggling(false);
    revokeAllSlotBlobs();
    setSlotFiles(emptyImageSlots());
    setSlotKeepUrls(emptyImageSlots());
    setSlotDisplayUrl(emptyImageSlots());
    setPdfFile(null);

    (async () => {
      try {
        const [config, cats, mars, unidadesData, prod] = await Promise.all([
          obtenerConfiguracionTiendaService().catch(() => null),
          obtenerCategoriasActivas().catch(() => []),
          getMarcas().catch(() => []),
          obtenerUnidadesPesoActivas().catch(() => []),
          isEdit ? getProductoById(productoId).catch(() => null) : Promise.resolve(null),
        ]);

        if (!alive) return;

        if (config?.ok && config?.data) setConfiguracionTienda(config.data);
        setCategorias(Array.isArray(cats) ? cats : []);
        setMarcas(Array.isArray(mars) ? mars : []);
        setUnidades(Array.isArray(unidadesData) ? unidadesData : []);

        if (isEdit && prod && typeof prod === "object") {
          setProduct(prod);

          const nextForm = {
            nombre: prod.nombre ?? "",
            descripcion: prod.descripcion ?? "",
            modelo: prod.modelo ?? "",
            no_parte: prod.no_parte ?? "",
            categoria_producto_id: prod.categoria_producto_id ?? "",
            subcategoria_id: prod.subcategoria_id ?? "",
            marca_producto_id: prod.marca_producto_id ?? "",
            precio_unitario: prod.precio_unitario ?? "",
            core_charge: prod.core_charge ?? "",
            oferta_activa: Number(prod.oferta_activa) === 1,
            oferta_tipo: prod.oferta_tipo || "porcentaje",
            oferta_valor: prod.oferta_valor ?? "",
            oferta_inicio: toDatetimeLocal(prod.oferta_inicio),
            oferta_fin: toDatetimeLocal(prod.oferta_fin),
            peso: prod.peso ?? "",
            unidad_peso_id: prod.unidad_peso_id ?? prod.unidad_id ?? "",
            estado: Number(prod.estado ?? 0),
            destacado: Number(prod.destacado) === 1,
          };
          setForm(nextForm);

          const rawImgs =
            Array.isArray(prod.imagenes) && prod.imagenes.length > 0
              ? prod.imagenes
              : prod.url_image
              ? [prod.url_image]
              : [];
          const nextKeep = emptyImageSlots();
          const nextDisp = emptyImageSlots();
          for (let i = 0; i < IMAGE_SLOT_COUNT; i++) {
            if (rawImgs[i]) {
              nextKeep[i] = rawImgs[i];
              nextDisp[i] = fileURL(rawImgs[i]);
            }
          }
          setSlotKeepUrls(nextKeep);
          setSlotDisplayUrl(nextDisp);

          if (nextForm.categoria_producto_id) {
            cargarSubcategorias(nextForm.categoria_producto_id);
          }
        } else if (isEdit) {
          setAlert({
            type: "error",
            message: "No se pudo cargar el producto solicitado.",
          });
        }
      } catch (err) {
        if (!alive) return;
        console.error("Error cargando el formulario de producto:", err);
        setAlert({
          type: "error",
          message: getNiceErrorMessage(err, "Error cargando la información del formulario."),
        });
      } finally {
        if (alive) setLoadingPage(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoId]);

  useEffect(() => {
    return () => {
      blobBySlotRef.current.forEach((u) => {
        if (u && String(u).startsWith("blob:")) URL.revokeObjectURL(u);
      });
    };
  }, []);

  useEffect(() => {
    if (loadingPage) return;
    if (!descriptionRef.current) return;
    if (descriptionRef.current.innerHTML !== (form.descripcion || "")) {
      descriptionRef.current.innerHTML = form.descripcion || "";
    }
  }, [loadingPage, form.descripcion]);

  useEffect(() => {
    if (loadingPage) return;
    if (form.categoria_producto_id) {
      cargarSubcategorias(form.categoria_producto_id);
    } else {
      setSubcategorias([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.categoria_producto_id, loadingPage]);

  const imageSlotsFilled = useMemo(() => {
    let n = 0;
    for (let i = 0; i < IMAGE_SLOT_COUNT; i++) {
      if (slotFiles[i] || slotKeepUrls[i] || slotDisplayUrl[i]) n += 1;
    }
    return n;
  }, [slotFiles, slotKeepUrls, slotDisplayUrl]);

  const skuPreview = useMemo(() => {
    if (isEdit && getProductSku(product)) return getProductSku(product);
    return slugifySkuPreview(form.nombre);
  }, [isEdit, product, form.nombre]);

  const isActive = Number(form.estado) === 1;
  const isBusy = saving || toggling;

  const goBack = () => {
    if (isBusy) return;
    navigate(PRODUCTS_LIST_PATH);
  };

  const handleImageSlot = (index, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const prevBlob = blobBySlotRef.current[index];
    if (prevBlob && String(prevBlob).startsWith("blob:")) URL.revokeObjectURL(prevBlob);

    const objectUrl = URL.createObjectURL(file);
    blobBySlotRef.current[index] = objectUrl;

    setSlotFiles((prev) => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
    setSlotKeepUrls((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setSlotDisplayUrl((prev) => {
      const next = [...prev];
      next[index] = objectUrl;
      return next;
    });
  };

  const clearImageSlot = (index) => {
    const prevBlob = blobBySlotRef.current[index];
    if (prevBlob && String(prevBlob).startsWith("blob:")) URL.revokeObjectURL(prevBlob);
    blobBySlotRef.current[index] = null;

    setSlotFiles((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setSlotKeepUrls((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setSlotDisplayUrl((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const handlePdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfFile(file);
  };

  const parseMoneyOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n;
  };

  const parseNumberOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n;
  };

  const normalizeHtml = (html) => {
    const clean = String(html || "").trim();
    if (!clean || clean === "<br>") return "";
    return clean;
  };

  const handleDescriptionInput = () => {
    const html = normalizeHtml(descriptionRef.current?.innerHTML || "");
    setForm((prev) => ({ ...prev, descripcion: html }));
  };

  const formatText = (command) => {
    document.execCommand(command, false, null);
    handleDescriptionInput();
    descriptionRef.current?.focus();
  };

  const validate = () => {
    if (!String(form.nombre || "").trim()) return "El nombre es obligatorio.";

    const price = parseMoneyOrNull(form.precio_unitario);
    if (price !== null && price < 0) return "El precio debe ser un número válido (>= 0).";

    const core = parseMoneyOrNull(form.core_charge);
    if (core !== null && core < 0) return "El Core Charge debe ser >= 0.";

    const w = parseNumberOrNull(form.peso);
    if (w !== null && w < 0) return "El peso debe ser >= 0.";

    if (form.oferta_activa) {
      const base = parseMoneyOrNull(form.precio_unitario);
      const valor = parseMoneyOrNull(form.oferta_valor);

      if (valor === null) return "Ingresa el valor de la oferta.";

      if (form.oferta_tipo === "porcentaje") {
        if (!(valor > 0 && valor < 100)) return "El porcentaje de descuento debe estar entre 1 y 99.";
      } else if (form.oferta_tipo === "precio") {
        if (!(valor > 0)) return "El precio de oferta debe ser mayor que 0.";
        if (base !== null && valor >= base)
          return "El precio de oferta debe ser menor que el precio normal.";
      } else {
        return "Selecciona el tipo de oferta.";
      }

      if (form.oferta_inicio && form.oferta_fin && form.oferta_fin < form.oferta_inicio) {
        return "La fecha de fin de la oferta no puede ser anterior a la de inicio.";
      }
    }

    return null;
  };

  const buildFormData = (payloadForm, includeFiles = true, isEditMode = false) => {
    const fd = new FormData();

    const normalized = {
      ...payloadForm,
      descripcion: normalizeHtml(payloadForm.descripcion),
      precio_unitario: parseMoneyOrNull(payloadForm.precio_unitario),
      core_charge: parseMoneyOrNull(payloadForm.core_charge),
      oferta_activa: payloadForm.oferta_activa ? 1 : 0,
      oferta_valor: parseMoneyOrNull(payloadForm.oferta_valor),
      peso: parseNumberOrNull(payloadForm.peso),
      estado: Number(payloadForm.estado ?? 0),
      destacado: payloadForm.destacado ? 1 : 0,
    };

    Object.entries(normalized).forEach(([k, v]) => {
      if (
        isEditMode &&
        (k === "categoria_producto_id" ||
          k === "subcategoria_id" ||
          k === "marca_producto_id" ||
          k === "unidad_peso_id")
      ) {
        const s = String(v ?? "").trim();
        fd.append(k, s);
        return;
      }

      fd.append(k, v === null || v === undefined ? "" : String(v));
    });

    if (includeFiles) {
      for (let i = 0; i < IMAGE_SLOT_COUNT; i++) {
        if (slotFiles[i]) {
          fd.append(`imagen_${i}`, slotFiles[i]);
        } else if (isEditMode) {
          fd.append(`keep_imagen_${i}`, slotKeepUrls[i] ?? "");
        }
      }
      if (pdfFile) fd.append("documento", pdfFile);
    }

    return fd;
  };

  const handleToggleStatus = async () => {
    if (!isEdit || toggling || saving || !product) return;

    const nextEstado = Number(form.estado) === 1 ? 0 : 1;

    try {
      setAlert(null);
      setToggling(true);

      const payload = { ...form, estado: nextEstado };
      const fd = buildFormData(payload, false, true);

      await actualizarProducto(product.producto_id, fd);

      setForm(payload);

      setAlert({
        type: "info",
        message: `El producto ahora está ${nextEstado === 1 ? "Activo" : "Inactivo"}.`,
      });
    } catch (err) {
      console.error("Error cambiando estado del producto:", err);
      setAlert({
        type: "error",
        message: getNiceErrorMessage(err, "Error actualizando el estado del producto."),
      });
    } finally {
      setToggling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || toggling) return;

    const msg = validate();
    if (msg) {
      setAlert({ type: "error", message: msg });
      window.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setAlert(null);
      setSaving(true);

      const payload = {
        ...form,
        estado: isEdit ? Number(form.estado ?? 0) : 0,
      };

      const fd = buildFormData(payload, true, isEdit);

      if (isEdit) await actualizarProducto(product.producto_id, fd);
      else await crearProducto(fd);

      navigate(PRODUCTS_LIST_PATH);
    } catch (err) {
      console.error("Error guardando producto:", err);
      setAlert({
        type: "error",
        message: getNiceErrorMessage(err, "Error guardando el producto."),
      });
    } finally {
      setSaving(false);
    }
  };

  const ofertaPreview = (() => {
    const base = parseMoneyOrNull(form.precio_unitario);
    const valor = parseMoneyOrNull(form.oferta_valor);
    if (!form.oferta_activa || base === null || valor === null) return null;

    let precioFinal = null;
    let pct = null;
    if (form.oferta_tipo === "porcentaje" && valor > 0 && valor < 100) {
      precioFinal = Math.round(base * (1 - valor / 100) * 100) / 100;
      pct = Math.round(valor);
    } else if (form.oferta_tipo === "precio" && valor > 0 && valor < base) {
      precioFinal = Math.round(valor * 100) / 100;
      pct = base > 0 ? Math.round(((base - valor) / base) * 100) : 0;
    }
    if (precioFinal === null) return null;
    return { base, precioFinal, pct };
  })();

  const fmtMoney = (n) =>
    `${simboloMoneda ? simboloMoneda + " " : ""}${Number(n).toLocaleString("es", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="pfp-page pfm-scope">
      <form onSubmit={handleSubmit} className="pfp-shell">
        <header className="pfp-topbar">
          <div className="pfp-topbar__left">
            <button
              type="button"
              className="pfp-back"
              onClick={goBack}
              disabled={isBusy}
              aria-label="Volver a productos"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span>Productos</span>
            </button>

            <div className="pfp-topbar__heading">
              <span className="pfp-topbar__badge">
                {isEdit ? "Edición" : "Nuevo producto"}
              </span>
              <div className="pfp-topbar__titlerow">
                <h1 className="pfp-topbar__title">
                  {isEdit ? form.nombre || "Editar producto" : "Crear producto"}
                </h1>
                {isEdit && (
                  <span
                    className={`pfp-status-pill${isActive ? " is-active" : " is-inactive"}`}
                  >
                    <span className="pfp-status-pill__dot" />
                    {isActive ? "Activo" : "Inactivo"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="pfp-scroll">
          <div className="pfp-container">
            {alert && (
              <div
                className={`pfm-alert ${
                  alert.type === "error" ? "pfm-alert--error" : "pfm-alert--info"
                }`}
              >
                <span>{alert.message}</span>
                <button
                  type="button"
                  className="pfm-alert__close"
                  onClick={() => setAlert(null)}
                  disabled={isBusy}
                  aria-label="Cerrar alerta"
                >
                  ✕
                </button>
              </div>
            )}

            {loadingPage ? (
              <div className="pfp-loading">
                <span className="pfp-loading__spinner" aria-hidden />
                <span>Cargando información del producto…</span>
              </div>
            ) : (
              <div className="pfm-grid">
                <div className="pfm-pane">
                  <div className="pfm-card">
                    <h3 className="pfm-section-title">Información principal</h3>
                    <p className="pfm-section-lead">
                      Nombre, identificadores y clasificación del producto en tu catálogo.
                    </p>

                    <Input
                      label="Nombre del producto *"
                      name="nombre"
                      value={form.nombre}
                      onChange={updateField}
                      disabled={isBusy}
                      placeholder="Ej. Filtro de aceite premium"
                    />

                    <div className="pfm-sku-box">
                      <span className="pfm-sku-box__label">
                        {isEdit ? "SKU del producto" : "Vista previa del SKU"}
                      </span>
                      <span className="pfm-sku-box__value">{skuPreview || "—"}</span>
                      <p className="pfm-sku-box__hint">
                        {isEdit
                          ? "Se actualiza automáticamente si modificás el nombre y guardás."
                          : "Se confirma al crear. Si ya existe uno similar, el sistema agrega sufijo (-2, -3…)."}
                      </p>
                    </div>

                    <div className="pfm-form-row pfm-form-row--3">
                      <SearchSelect
                        label="Categoría"
                        name="categoria_producto_id"
                        value={form.categoria_producto_id}
                        onChange={updateField}
                        disabled={isBusy}
                        options={categorias.map((c) => ({
                          value: c.categoria_producto_id,
                          label: c.nombre,
                        }))}
                        placeholder="Selecciona categoría..."
                        searchPlaceholder="Buscar categoría..."
                        maxListHeight={260}
                      />

                      <SearchSelect
                        label="Subcategoría"
                        name="subcategoria_id"
                        value={form.subcategoria_id}
                        onChange={updateField}
                        disabled={isBusy || !form.categoria_producto_id || loadingSubcategorias}
                        options={subcategorias.map((s) => ({
                          value: s.subcategorias_id,
                          label: s.nombre,
                        }))}
                        placeholder={
                          !form.categoria_producto_id
                            ? "Primero selecciona categoría..."
                            : loadingSubcategorias
                            ? "Cargando subcategorías..."
                            : "Selecciona subcategoría..."
                        }
                        searchPlaceholder="Buscar subcategoría..."
                        maxListHeight={260}
                      />

                      <SearchSelect
                        label="Marca"
                        name="marca_producto_id"
                        value={form.marca_producto_id}
                        onChange={updateField}
                        disabled={isBusy}
                        options={marcas.map((m) => ({
                          value: m.marca_producto_id,
                          label: m.nombre,
                        }))}
                        placeholder="Selecciona marca..."
                        searchPlaceholder="Buscar marca..."
                        maxListHeight={260}
                      />
                    </div>

                    <Input
                      label="Modelo"
                      name="modelo"
                      value={form.modelo}
                      onChange={updateField}
                      disabled={isBusy}
                      placeholder="Referencia o modelo comercial"
                    />

                    <Input
                      label="Número de parte (opcional)"
                      name="no_parte"
                      value={form.no_parte}
                      onChange={updateField}
                      disabled={isBusy}
                      placeholder="Código del fabricante (OEM), distinto del SKU"
                    />
                  </div>

                  <div className="pfm-card">
                    <h3 className="pfm-section-title">Descripción del producto</h3>

                    <label className="pfm-label">Descripción enriquecida</label>

                    <div className="pfm-editor">
                      <div className="pfm-editor__toolbar">
                        <ToolbarButton type="button" onClick={() => formatText("bold")} disabled={isBusy}>
                          <strong>B</strong>
                        </ToolbarButton>

                        <ToolbarButton type="button" onClick={() => formatText("italic")} disabled={isBusy}>
                          <em>I</em>
                        </ToolbarButton>

                        <ToolbarButton type="button" onClick={() => formatText("underline")} disabled={isBusy}>
                          <span className="pfm-editor__underline">U</span>
                        </ToolbarButton>

                        <ToolbarDivider />

                        <ToolbarButton
                          type="button"
                          onClick={() => formatText("insertUnorderedList")}
                          disabled={isBusy}
                        >
                          • Lista
                        </ToolbarButton>

                        <ToolbarButton
                          type="button"
                          onClick={() => formatText("insertOrderedList")}
                          disabled={isBusy}
                        >
                          1. Lista
                        </ToolbarButton>
                      </div>

                      <div
                        ref={descriptionRef}
                        className="pfm-editor__content pfp-editor__content"
                        contentEditable={!isBusy}
                        suppressContentEditableWarning
                        onInput={handleDescriptionInput}
                        data-placeholder="Escribe una descripción clara y atractiva del producto..."
                      />
                    </div>
                  </div>

                  <div className="pfm-card">
                    <h3 className="pfm-section-title">Precio y peso</h3>

                    <div className="pfm-form-row pfm-form-row--4">
                      <Input
                        label={simboloMoneda ? `Precio (${simboloMoneda})` : "Precio"}
                        type="number"
                        step="0.01"
                        name="precio_unitario"
                        value={form.precio_unitario}
                        onChange={updateField}
                        disabled={isBusy}
                        placeholder="0.00"
                      />

                      <Input
                        label={simboloMoneda ? `Core Charge (${simboloMoneda})` : "Core Charge"}
                        type="number"
                        step="0.01"
                        name="core_charge"
                        value={form.core_charge}
                        onChange={updateField}
                        disabled={isBusy}
                        placeholder="0.00"
                      />

                      <Input
                        label="Peso"
                        type="number"
                        step="0.01"
                        name="peso"
                        value={form.peso}
                        onChange={updateField}
                        disabled={isBusy}
                        placeholder="0.00"
                      />

                      <SearchSelect
                        label="Unidad"
                        name="unidad_peso_id"
                        value={form.unidad_peso_id}
                        onChange={updateField}
                        disabled={isBusy}
                        options={unidades.map((u) => ({
                          value: u.unidad_peso_id,
                          label: u.abreviatura || u.nombre,
                        }))}
                        placeholder="Selecciona unidad..."
                        searchPlaceholder="Buscar unidad..."
                        maxListHeight={220}
                      />
                    </div>
                  </div>

                  <div className="pfm-card">
                    <div className="pfp-oferta__head">
                      <div>
                        <h3 className="pfm-section-title">Oferta / Descuento</h3>
                        <p className="pfm-section-lead">
                          Rebaja el precio de este producto. Actívala ya o prográmala con fechas.
                        </p>
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!form.oferta_activa}
                        className={`pfp-switch${form.oferta_activa ? " is-on" : ""}`}
                        onClick={() =>
                          !isBusy && setForm((p) => ({ ...p, oferta_activa: !p.oferta_activa }))
                        }
                        disabled={isBusy}
                      >
                        <span className="pfp-switch__track">
                          <span className="pfp-switch__thumb" />
                        </span>
                        <span className="pfp-switch__label">
                          {form.oferta_activa ? "En oferta" : "Sin oferta"}
                        </span>
                      </button>
                    </div>

                    {form.oferta_activa && (
                      <div className="pfp-oferta__body">
                        <div className="pfp-seg" role="group" aria-label="Tipo de descuento">
                          <button
                            type="button"
                            className={`pfp-seg__btn${
                              form.oferta_tipo === "porcentaje" ? " is-active" : ""
                            }`}
                            onClick={() => setForm((p) => ({ ...p, oferta_tipo: "porcentaje" }))}
                            disabled={isBusy}
                          >
                            Porcentaje (%)
                          </button>
                          <button
                            type="button"
                            className={`pfp-seg__btn${
                              form.oferta_tipo === "precio" ? " is-active" : ""
                            }`}
                            onClick={() => setForm((p) => ({ ...p, oferta_tipo: "precio" }))}
                            disabled={isBusy}
                          >
                            Precio fijo
                          </button>
                        </div>

                        <div className="pfm-form-row pfm-form-row--3">
                          <Input
                            label={
                              form.oferta_tipo === "porcentaje"
                                ? "Descuento (%)"
                                : `Precio de oferta${simboloMoneda ? ` (${simboloMoneda})` : ""}`
                            }
                            type="number"
                            step={form.oferta_tipo === "porcentaje" ? "1" : "0.01"}
                            name="oferta_valor"
                            value={form.oferta_valor}
                            onChange={updateField}
                            disabled={isBusy}
                            placeholder={form.oferta_tipo === "porcentaje" ? "Ej. 20" : "0.00"}
                          />

                          <Input
                            label="Desde (opcional)"
                            type="datetime-local"
                            name="oferta_inicio"
                            value={form.oferta_inicio}
                            onChange={updateField}
                            disabled={isBusy}
                          />

                          <Input
                            label="Hasta (opcional)"
                            type="datetime-local"
                            name="oferta_fin"
                            value={form.oferta_fin}
                            onChange={updateField}
                            disabled={isBusy}
                          />
                        </div>

                        {ofertaPreview ? (
                          <div className="pfp-oferta__preview">
                            <span className="pfp-oferta__badge">−{ofertaPreview.pct}%</span>
                            <span className="pfp-oferta__old">{fmtMoney(ofertaPreview.base)}</span>
                            <span className="pfp-oferta__arrow" aria-hidden>
                              →
                            </span>
                            <span className="pfp-oferta__new">{fmtMoney(ofertaPreview.precioFinal)}</span>
                          </div>
                        ) : (
                          <p className="pfp-oferta__hint">
                            Completa el descuento para ver el precio final.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pfm-pane pfm-pane--side">
                  <div className="pfm-card">
                    <h3 className="pfm-section-title">Multimedia</h3>

                    <label className="pfm-label">
                      Imágenes del producto ({imageSlotsFilled}/{IMAGE_SLOT_COUNT})
                    </label>
                    <div className="pfm-image-grid">
                      {Array.from({ length: IMAGE_SLOT_COUNT }, (_, idx) => (
                        <div key={idx} className="pfm-image-slot">
                          <div
                            className={`pfm-image-upload${isBusy ? " is-disabled" : ""}`}
                            onClick={() =>
                              !isBusy && document.getElementById(`img-input-${idx}`)?.click()
                            }
                          >
                            {slotDisplayUrl[idx] ? (
                              <img
                                src={slotDisplayUrl[idx]}
                                alt={`Imagen ${idx + 1}`}
                                className="pfm-image-upload__img"
                              />
                            ) : (
                              <div className="pfm-image-upload__placeholder">
                                <span className="pfm-image-upload__icon">＋</span>
                                <span>Slot {idx + 1}</span>
                              </div>
                            )}
                          </div>
                          {slotDisplayUrl[idx] && (
                            <button
                              type="button"
                              className="pfm-image-slot__clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isBusy) clearImageSlot(idx);
                              }}
                              disabled={isBusy}
                            >
                              Quitar
                            </button>
                          )}
                          <input
                            id={`img-input-${idx}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageSlot(idx, e)}
                            className="pfm-sr-only"
                            disabled={isBusy}
                          />
                        </div>
                      ))}
                    </div>

                    <label className="pfm-label pfm-label--spaced">Documento PDF</label>
                    <div
                      className={`pfm-pdf-upload${isBusy ? " is-disabled" : ""}`}
                      onClick={() => !isBusy && document.getElementById("pdf-input")?.click()}
                    >
                      <div className="pfm-pdf-upload__inner">
                        <span className="pfm-pdf-upload__icon" aria-hidden>
                          📄
                        </span>
                        <span className="pfm-pdf-upload__name">
                          {pdfFile
                            ? pdfFile.name
                            : product?.url_documento
                            ? String(product.url_documento).split("/").pop()
                            : "Haz clic para subir PDF"}
                        </span>
                      </div>
                    </div>

                    <input
                      id="pdf-input"
                      type="file"
                      accept="application/pdf"
                      onChange={handlePdf}
                      className="pfm-sr-only"
                      disabled={isBusy}
                    />
                  </div>

                  <div className="pfm-card">
                    <div className="pfp-oferta__head">
                      <div>
                        <h3 className="pfm-section-title">Producto destacado</h3>
                        <p className="pfm-section-lead">
                          Los productos destacados aparecen al azar en la sección de inicio de tu tienda.
                        </p>
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!form.destacado}
                        className={`pfp-switch${form.destacado ? " is-on" : ""}`}
                        onClick={() =>
                          !isBusy && setForm((p) => ({ ...p, destacado: !p.destacado }))
                        }
                        disabled={isBusy}
                      >
                        <span className="pfp-switch__track">
                          <span className="pfp-switch__thumb" />
                        </span>
                        <span className="pfp-switch__label">
                          {form.destacado ? "Destacado" : "No destacado"}
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="pfm-card">
                    <h3 className="pfm-section-title">Resumen rápido</h3>

                    <SummaryRow label="Nombre" value={form.nombre || "Sin definir"} />
                    <SummaryRow label="SKU" value={skuPreview || "Sin definir"} />
                    <SummaryRow
                      label="Categoría"
                      value={
                        categorias.find(
                          (x) => String(x.categoria_producto_id) === String(form.categoria_producto_id)
                        )?.nombre || "Sin definir"
                      }
                    />
                    <SummaryRow
                      label="Subcategoría"
                      value={
                        subcategorias.find(
                          (x) => String(x.subcategorias_id) === String(form.subcategoria_id)
                        )?.nombre || "Sin definir"
                      }
                    />
                    <SummaryRow
                      label="Estado"
                      value={isEdit ? (isActive ? "Activo" : "Inactivo") : "Inactivo al crear"}
                    />

                    {isEdit && (
                      <div className="pfm-status-bar">
                        <span
                          className={`pfm-status-bar__dot${isActive ? " is-active" : " is-inactive"}`}
                        />
                        <span className="pfm-status-bar__text">{isActive ? "Activo" : "Inactivo"}</span>

                        <button
                          type="button"
                          onClick={handleToggleStatus}
                          disabled={isBusy}
                          className={`pfm-status-bar__btn${
                            isActive ? " pfm-status-bar__btn--danger" : " pfm-status-bar__btn--success"
                          }`}
                        >
                          {toggling ? "Actualizando..." : isActive ? "Desactivar" : "Activar"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="pfp-footer">
          <button type="button" className="pfm-btn-cancel" onClick={goBack} disabled={isBusy}>
            Cancelar
          </button>
          <button type="submit" className="pfm-btn-save" disabled={isBusy || loadingPage}>
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear producto"}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default ProductFormPage;
